import { FUNCTIONS_URL, getIdToken } from '../firebase';

// 基礎 API 呼叫函式
async function apiCall(endpoint, options = {}) {
  const url = `${FUNCTIONS_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || '請求失敗');
    err.code = body.code;
    err.status = response.status;
    throw err;
  }

  return response.json();
}

// 帶認證的 API 呼叫函式
async function authApiCall(endpoint, options = {}) {
  const token = await getIdToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${FUNCTIONS_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || '請求失敗');
    err.code = body.code;
    err.status = response.status;
    throw err;
  }

  return response.json();
}

// ===== 健康檢查 API =====
export const healthCheck = async () => {
  try {
    const data = await apiCall('/');
    return {
      status: data.status === 'running' ? 'healthy' : 'unhealthy',
      google_sheets_api: 'available' // 暫時回傳 available，後續實作時再調整
    };
  } catch {
    return {
      status: 'unhealthy',
      google_sheets_api: 'unavailable'
    };
  }
};

// ===== 儀表板 API =====
export const dashboardAPI = {
  // 取得供應商填答排行榜
  getRankings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/dashboard/rankings${query ? '?' + query : ''}`);
  },

  // 取得儀表板統計
  getStats: () => {
    return authApiCall('/api/dashboard/stats');
  },

  // 取得最近回覆列表
  getRecentResponses: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/dashboard/recent-responses${query ? '?' + query : ''}`);
  }
};

// ===== 雲端報表庫 API =====
export const reportsAPI = {
  // 取得所有報表列表
  getAll: () => {
    return authApiCall('/api/reports');
  },

  // 取得單一報表資訊
  getById: (reportId) => {
    return authApiCall(`/api/reports/${reportId}`);
  },

  // 取得報表資料（支援分頁、篩選）
  getData: (reportId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/reports/${reportId}/data${query ? '?' + query : ''}`);
  },

  // 上傳新報表（僅管理員）
  upload: async (file, name, year, accessTags = []) => {
    const token = await getIdToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('year', year);
    if (accessTags.length > 0) {
      formData.append('access_tags', JSON.stringify(accessTags));
    }

    const response = await fetch(`${FUNCTIONS_URL}/api/reports`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '上傳失敗');
    }

    return response.json();
  },

  // 更新報表（僅管理員）
  update: (reportId, data) => {
    return authApiCall(`/api/reports/${reportId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  // 刪除報表（僅管理員）
  delete: (reportId) => {
    return authApiCall(`/api/reports/${reportId}`, {
      method: 'DELETE'
    });
  }
};

// ===== 專案供應商 API（子集合）=====
export const projectVendorsAPI = {
  // 取得專案內供應商列表
  getAll: (projectId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/projects/${projectId}/vendors${query ? '?' + query : ''}`);
  },

  // 取得供應商列表及其所有問卷指派狀態（供應商面向視圖）
  getWithAssignments: (projectId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/projects/${projectId}/vendors/with-assignments${query ? '?' + query : ''}`);
  },

  // 從報表匯入供應商
  importFromReport: (projectId, reportId, vendorIds, tags = []) => {
    return authApiCall(`/api/projects/${projectId}/vendors/import`, {
      method: 'POST',
      body: JSON.stringify({ reportId, vendorIds, tags })
    });
  },

  // 批量標籤操作
  bulkTag: (projectId, vendorIds, tags, action = 'add') => {
    return authApiCall(`/api/projects/${projectId}/vendors/bulk-tag`, {
      method: 'POST',
      body: JSON.stringify({ vendorIds, tags, action })
    });
  },

  // 更新單一供應商
  update: (projectId, vendorId, data) => {
    return authApiCall(`/api/projects/${projectId}/vendors/${vendorId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  // 刪除單一供應商
  delete: (projectId, vendorId) => {
    return authApiCall(`/api/projects/${projectId}/vendors/${vendorId}`, {
      method: 'DELETE'
    });
  }
};

// ===== 專案標籤 API（子集合）=====
export const projectTagsAPI = {
  // 取得專案內標籤列表
  getAll: (projectId) => {
    return authApiCall(`/api/projects/${projectId}/tags`);
  },

  // 建立專案內標籤
  create: (projectId, data) => {
    return authApiCall(`/api/projects/${projectId}/tags`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 更新標籤
  update: (projectId, tagId, data) => {
    return authApiCall(`/api/projects/${projectId}/tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  // 刪除標籤
  delete: (projectId, tagId) => {
    return authApiCall(`/api/projects/${projectId}/tags/${tagId}`, {
      method: 'DELETE'
    });
  }
};

// ===== 專案回覆 API（子集合）=====
export const projectResponsesAPI = {
  // 取得專案內回覆列表（支援 formId, vendorId, status 篩選）
  getAll: (projectId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/projects/${projectId}/responses${query ? '?' + query : ''}`);
  },

  // 取得單一回覆詳情
  getById: (projectId, responseId) => {
    return authApiCall(`/api/projects/${projectId}/responses/${responseId}`);
  },

  // 刪除回覆
  delete: (projectId, responseId) => {
    return authApiCall(`/api/projects/${projectId}/responses/${responseId}`, {
      method: 'DELETE'
    });
  },

  // 要求重填：目前答案改為舊版本並寫入操作紀錄（新連結另由 sendInvitations 寄出）
  resend: (projectId, responseId, reason) => {
    return authApiCall(`/api/projects/${projectId}/responses/${responseId}/resend`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || null })
    });
  },

  // 取得某廠商在某問卷的所有版本（含重填前的舊版本，不含已刪除）
  getVersions: (projectId, vendorId, formId) => {
    const query = new URLSearchParams({ vendorId, formId }).toString();
    return authApiCall(`/api/projects/${projectId}/responses/versions?${query}`);
  }
};

// ===== 專案紀錄 API（子集合）=====
export const projectLogsAPI = {
  // 取得專案紀錄
  getAll: (projectId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/projects/${projectId}/logs${query ? '?' + query : ''}`);
  }
};

// ===== 專案問卷 API（子集合）=====
export const projectFormsAPI = {
  // 取得專案內問卷列表
  getAll: (projectId) => {
    return authApiCall(`/api/projects/${projectId}/forms`);
  },

  // 取得單一問卷
  getOne: (projectId, formId) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}`);
  },

  // 建立問卷
  create: (projectId, data) => {
    return authApiCall(`/api/projects/${projectId}/forms`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 更新問卷
  update: (projectId, formId, data) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  // 刪除問卷
  delete: (projectId, formId) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}`, {
      method: 'DELETE'
    });
  },

  // ===== 問卷指派 =====

  // 取得問卷已指派的供應商列表
  getAssignments: (projectId, formId) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments`);
  },

  // 指派供應商到問卷
  createAssignments: (projectId, formId, data) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 依標籤批量指派
  assignByTag: (projectId, formId, data) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments/by-tag`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 更新指派設定（截止日期、提醒）
  updateAssignment: (projectId, formId, vendorId, data) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments/${vendorId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // 批量更新提醒設定
  bulkUpdateReminders: (projectId, formId, vendorIds, data) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments/bulk-reminders`, {
      method: 'PUT',
      body: JSON.stringify({ vendor_ids: vendorIds, ...data })
    });
  },

  // 移除指派
  removeAssignment: (projectId, formId, vendorId) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/assignments/${vendorId}`, {
      method: 'DELETE'
    });
  },

  // 批量發送問卷邀請
  sendInvitations: (projectId, formId, vendorIds) => {
    return authApiCall(`/api/projects/${projectId}/forms/${formId}/send-invitations`, {
      method: 'POST',
      body: JSON.stringify({ vendor_ids: vendorIds })
    });
  }
};

// ===== ESG問卷回覆 API =====
export const getResponses = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await authApiCall(`/api/responses${query ? '?' + query : ''}`);
  return {
    responses: response.data || [],
    total: response.count || 0
  };
};

export const submitResponse = async (data) => {
  return authApiCall('/api/responses', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const compareAllResponses = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return authApiCall(`/api/comparison${query ? '?' + query : ''}`);
};

export const responsesAPI = {
  // 取得問卷回覆列表
  getAll: (params = {}) => getResponses(params),

  // 取得單一問卷回覆
  getById: (id) => {
    return authApiCall(`/api/responses/${id}`);
  },

  // 提交問卷回覆
  submit: (data) => submitResponse(data),

  // 更新問卷回覆
  update: (id, data) => {
    return authApiCall(`/api/responses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // 刪除問卷回覆
  delete: (id) => {
    return authApiCall(`/api/responses/${id}`, {
      method: 'DELETE'
    });
  },

  // 比較問卷回覆
  compare: (params = {}) => compareAllResponses(params),

  // 比較特定表單的回覆
  compareForm: (formId) => {
    return authApiCall(`/api/comparison/${formId}`);
  },

  // 重發問卷（作廢舊回覆，產生新 Token）
  resend: (id, reason) => {
    return authApiCall(`/api/responses/${id}/resend`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }
};

// ===== 表單 API =====
export const getForms = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await authApiCall(`/api/forms${query ? '?' + query : ''}`);
  return {
    forms: response.data || [],
    total: response.count || 0
  };
};

export const createForm = async (data) => {
  return authApiCall('/api/forms', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const syncFromSheets = async (formId, projectId = null) => {
  const url = projectId
    ? `/api/sheets/sync/${formId}?projectId=${projectId}`
    : `/api/sheets/sync/${formId}`;
  return authApiCall(url, {
    method: 'POST'
  });
};

export const formsAPI = {
  // 取得表單列表
  getAll: (params = {}) => getForms(params),

  // 取得單一表單
  getById: (id) => {
    return authApiCall(`/api/forms/${id}`);
  },

  // 取得專案的問卷列表
  getByProject: async (projectId) => {
    const response = await authApiCall(`/api/projects/${projectId}/forms`);
    return {
      forms: response.data || [],
      total: response.count || 0
    };
  },

  // 建立表單
  create: (data) => createForm(data),

  // 更新表單
  update: (id, data) => {
    return authApiCall(`/api/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // 刪除表單
  delete: (id) => {
    return authApiCall(`/api/forms/${id}`, {
      method: 'DELETE'
    });
  },

  // 同步 Google Sheets
  sync: (formId) => syncFromSheets(formId),

  // 匯出到 Google Sheets
  export: (formId, targetSheetId, targetSheetName) => {
    return authApiCall(`/api/sheets/export/${formId}`, {
      method: 'POST',
      body: JSON.stringify({ targetSheetId, targetSheetName })
    });
  },

  // 驗證 Google Sheets 連線
  validateSheet: (sheetId) => {
    return authApiCall(`/api/sheets/validate/${sheetId}`);
  },

  // 取得表單的供應商填寫狀態
  getVendorStatuses: (formId) => {
    return authApiCall(`/api/forms/${formId}/vendor-statuses`);
  }
};

// ===== 提醒 API =====
export const remindersAPI = {
  // 取得所有提醒設定
  getAll: () => {
    return authApiCall('/api/reminders');
  },

  // 取得單一提醒設定
  getById: (id) => {
    return authApiCall(`/api/reminders/${id}`);
  },

  // 取得特定供應商的所有提醒
  getByVendor: (vendorId) => {
    return authApiCall(`/api/reminders/vendor/${vendorId}`);
  },

  // 建立提醒設定
  create: (data) => {
    return authApiCall('/api/reminders', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 更新提醒設定
  update: (id, data) => {
    return authApiCall(`/api/reminders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // 刪除提醒設定
  delete: (id) => {
    return authApiCall(`/api/reminders/${id}`, {
      method: 'DELETE'
    });
  },

  // 發送測試提醒信
  sendTest: (id) => {
    return authApiCall(`/api/reminders/send-test/${id}`, {
      method: 'POST'
    });
  }
};

// vendorTagsAPI 已移除 - 供應商標籤現在是專案子集合，使用 projectTagsAPI

// importAPI 已移除 - 供應商匯入改為從報表庫匯入到專案，使用 projectVendorsAPI.importFromReport

// ===== 系統監控 API =====
export const monitoringAPI = {
  // 取得系統統計
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authApiCall(`/api/monitoring/stats${query ? '?' + query : ''}`);
  },

  // 取得監控用的專案列表（需認證以根據權限過濾）
  getProjects: () => {
    return authApiCall('/api/monitoring/projects');
  },

  // 取得同步歷史 (支援專案篩選)
  getSyncHistory: (params = {}) => {
    // 兼容舊版只傳數字的情況
    const queryParams = typeof params === 'number' ? { limit: params } : params;
    const query = new URLSearchParams(queryParams).toString();
    return authApiCall(`/api/monitoring/sync-history${query ? '?' + query : ''}`);
  },

  // 取得提醒記錄 (支援專案篩選)
  getReminderLogs: (params = {}) => {
    // 兼容舊版只傳數字的情況
    const queryParams = typeof params === 'number' ? { limit: params } : params;
    const query = new URLSearchParams(queryParams).toString();
    return authApiCall(`/api/monitoring/reminder-logs${query ? '?' + query : ''}`);
  },

};

// esgAPI 已移除 - ESG 年度管理功能整合到專案管理中

// ===== 審計日誌 API =====
export const activityLogsAPI = {
  // 取得所有活動日誌（需要傳入 auth header）
  getAll: async (params = {}, authHeader = {}) => {
    const query = new URLSearchParams();
    if (params.activity_type) query.append('activity_type', params.activity_type);
    if (params.user_id) query.append('user_id', params.user_id);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.limit) query.append('limit', params.limit);
    if (params.offset) query.append('offset', params.offset);

    const response = await fetch(`${FUNCTIONS_URL}/api/activity-logs?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || '獲取活動日誌失敗');
    }

    return response.json();
  },

  // 取得專案的活動日誌
  getByProject: async (projectId, params = {}, authHeader = {}) => {
    const query = new URLSearchParams();
    if (params.activity_type) query.append('activity_type', params.activity_type);
    if (params.limit) query.append('limit', params.limit);
    if (params.offset) query.append('offset', params.offset);

    const response = await fetch(`${FUNCTIONS_URL}/api/projects/${projectId}/activity-logs?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || '獲取專案活動日誌失敗');
    }

    return response.json();
  },
};

// projectAssignmentsAPI 已移除 - 專案資源管理功能整合到專案子集合中

// ===== 權限標籤 API =====
export const permissionTagsAPI = {
  // 取得所有權限標籤（需要認證）
  getAll: (includeInactive = false) => {
    const query = includeInactive ? '?include_inactive=true' : '';
    return authApiCall(`/api/permission-tags${query}`);
  },

  // 取得單一權限標籤（需要認證）
  getById: (id) => {
    return authApiCall(`/api/permission-tags/${id}`);
  },

  // 建立權限標籤（僅管理員）
  create: (data) => {
    return authApiCall('/api/permission-tags', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // 更新權限標籤（僅管理員）
  update: (id, data) => {
    return authApiCall(`/api/permission-tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // 刪除（停用）權限標籤（僅管理員）
  delete: (id, force = false) => {
    const query = force ? '?force=true' : '';
    return authApiCall(`/api/permission-tags/${id}${query}`, {
      method: 'DELETE'
    });
  },

  // 取得權限標籤關聯的資源（僅管理員）
  getResources: (id) => {
    return authApiCall(`/api/permission-tags/${id}/resources`);
  },

  // 取得可指派的資源列表（僅管理員）
  getAvailableResources: (id, type = null) => {
    const query = type ? `?type=${type}` : '';
    return authApiCall(`/api/permission-tags/${id}/available-resources${query}`);
  },

  // 批量更新資源指派（僅管理員）
  updateResources: (id, data) => {
    return authApiCall(`/api/permission-tags/${id}/resources`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
};

// 導出 authApiCall 供其他模組使用
export { authApiCall };
